import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { TextField } from '@/components/ui/text-field';
import sent from '@/assets/sent.svg';
import { EMAIL_PATTERN } from '@/lib/validation';

import { requestPasswordReset } from '../api/auth-api';
import { FORM_ERROR, errorMessage } from '../use-auth-error';

interface ForgotForm {
  email: string;
}

export function ForgotPasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} title="Forgot your password?" hideTitle>
      <ForgotBody onClose={onClose} />
    </Dialog>
  );
}

function ForgotBody({ onClose }: { onClose: () => void }) {
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitted },
  } = useForm<ForgotForm>();

  const mutation = useMutation({ mutationFn: (v: ForgotForm) => requestPasswordReset(v.email) });

  if (mutation.isSuccess) {
    return (
      <div className="stack center">
        <img src={sent} alt="" className="art" style={{ margin: '0 auto' }} />
        <h4>We&apos;ve sent an email to {getValues('email')}</h4>
        <p>You&apos;ll receive a link to reset your password.</p>
        <Button block onClick={onClose}>
          Close
        </Button>
        <p className="muted">
          Didn&apos;t receive an email?{' '}
          <button
            type="button"
            className="link-button"
            onClick={() => {
              mutation.mutate(getValues());
            }}
          >
            Resend
          </button>
        </p>
      </div>
    );
  }

  const hasFieldErrors = isSubmitted && Object.keys(errors).length > 0;
  return (
    <form
      className="stack"
      noValidate
      onSubmit={(e) =>
        void handleSubmit((v) => {
          mutation.mutate(v);
        })(e)
      }
    >
      <h3>Forgot your password?</h3>
      <p>Complete the following to receive a password reset email.</p>
      {mutation.isError && <Alert>{errorMessage(mutation.error)}</Alert>}
      {!mutation.isError && hasFieldErrors && <Alert>{FORM_ERROR}</Alert>}
      <TextField
        id="forgotPassword-email"
        label="Email Address"
        type="email"
        autoComplete="username"
        error={errors.email?.message}
        {...register('email', {
          required: 'Please provide a valid email address',
          pattern: { value: EMAIL_PATTERN, message: 'Please provide a valid email address' },
        })}
      />
      <Button type="submit" block busy={mutation.isPending}>
        Submit
      </Button>
    </form>
  );
}
